import {BizKeys, VideoPlayerTypes} from "../../../core/constants/enums";
import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {Col, Container, Row} from "reactstrap";
import {JsonContentContext} from "../../contexts";
import BizKeySection from "./bizkey-section";
import FormSection from "./form-section";
import EnvService from "../../../core/services/env";
import useIsMounted from "../../hooks/use-is-mounted";
import VideoPlayer from "../../components/base/video-player";
import {ReactComponent as InfiniteKey} from "../../../assets/images/login-page/infinite_key.svg";
import {ReactComponent as ActKey} from "../../../assets/images/login-page/act_key.svg";
import {ReactComponent as AimKey} from "../../../assets/images/login-page/aim_key.svg";
import classnames from "classnames";

// The Max-Height of this page
export const maxHeight = 690;
// The Min-Height of this page
export const minHeight = 535;
// The Height of the Line Container
export const lineContainerHeight = 40;
// The Height of the line. It must be less than lineContainerHeight
export const lineHeight = 6;
// The Width of the Line
export const lineWidth = 25;
// The height of each of the Biz Principle images
export const logoWidth = 210;
// The duration in which the debounce wait before changing the
export const changeLineNumbersWaitDuration = 300;
// The duration in which the debounce wait before the changing the color of lines.
export const changeLineColorsWaitDuration = 50;


/**
 * Randomly chooses a new title and message for each of the biz phases and returns the new data.
 * @param jsonContent
 * @return {*&{bizkey: {actKey, aimKey, infiniteKey}}}
 */
const selectContentForBizPhases = (jsonContent) => {
    if (Array.isArray(jsonContent?.bizkey?.aimKey)) {
        return {
            ...jsonContent,
            bizkey: {
                aimKey: jsonContent.bizkey.aimKey[Math.trunc(Math.random() * (jsonContent.bizkey.aimKey.length - 1))],
                actKey: jsonContent.bizkey.actKey[Math.trunc(Math.random() * (jsonContent.bizkey.actKey.length - 1))],
                infiniteKey: jsonContent.bizkey.infiniteKey[Math.trunc(Math.random() * (jsonContent.bizkey.infiniteKey.length - 1))],
            }
        }
    }
    return jsonContent;
}

const BaseAuthView = ({children}) => {
    const [selectedKey, setSelectedKey] = useState(BizKeys[Object.keys(BizKeys)[Math.round(Math.random() * 2)]]);
    const jsonContent = useContext(JsonContentContext);
    const [selectedJsonContent, setSelectedJsonContent] = useState(() => selectContentForBizPhases(jsonContent));
    const containerRef = useRef();

    useEffect(() => {
        setSelectedJsonContent(selectContentForBizPhases(jsonContent))
    }, [jsonContent])

    /**
     * Sets the newKey as the selected key if the new key is not the same as the selected key
     * @param newKey
     * @param doNotSetNewContent
     */
    const selectNewKey = (newKey, doNotSetNewContent) => {
        if (newKey === selectedKey) return;
        setSelectedKey(newKey);
        if (!doNotSetNewContent)
            setSelectedJsonContent(selectContentForBizPhases(jsonContent))
    }

    return (
        <>
            <Container className={'w-100 vh-100'}>
                <Row className={'h-100'}>
                    <Col xs={12} lg={4} xl={3} className={'h-100 d-flex justify-content-center align-items-center'}>
                        <div ref={containerRef} className={'h-100 d-flex justify-content-center align-items-center'}>
                            <FormSection containerRef={containerRef} selectedKey={selectedKey}>
                                {children}
                            </FormSection>
                        </div>
                    </Col>
                    <Col xs={0} lg={8} xl={9} className={'h-100 d-none d-lg-block'}>
                        <Row className={'h-100'}>
                            <Col className={'d-flex justify-content-center align-items-center'}>
                                {
                                    EnvService.useBizkeytechLogin &&
                                    <BizKeySection
                                        jsonContent={selectedJsonContent}
                                        selectedKey={selectedKey}
                                        onKeyClicked={selectNewKey}
                                    />
                                }
                                {
                                    EnvService.useBizLearnLogin &&
                                    <BizLearnSection
                                        selectedKey={selectedKey}
                                        jsonContent={selectedJsonContent}
                                        onKeyClicked={selectNewKey}
                                    />
                                }
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </>
    );
}

const BizLearnSection = ({jsonContent, selectedKey, onKeyClicked}) => {
    const [video, setVideo] = useState(null);
    const [loading, setLoading] = useState(true);
    const isMounted = useIsMounted();

    const getVideo = useCallback(async () => {
        setLoading(true);
        await new Promise(resolve => setTimeout(resolve, 2000));
        // TODO: Fetch the video from the server
        const response = {
            resultFlag: true,
            data: {
                url: 'https://www.youtube.com/embed/B97hedf7O6k?si=21jyAnlp9O8wksBc',
                title: 'BizLearn Video',
            },
        }
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setVideo(response.data);
        }
    }, [isMounted])

    /**
     * When the component mounts, fetch the video from the server.
     */
    useEffect(() => {
        getVideo().then();
    }, []);

    return (
        <div className={'h-100 w-100'}
             style={{
                 minHeight: minHeight,
                 maxHeight: maxHeight,
             }}
        >
            {
                loading &&
                <div className={'loading-div biz-learn-video'}>
                    <div/>
                </div>
            }
            {
                !loading &&
                <div
                    className={'w-100'}
                >
                    <VideoPlayer
                        id={'biz-learn-video'}
                        url={video?.url}
                        title={video?.title ?? "Video Player"}
                        type={VideoPlayerTypes.large}
                    />
                </div>
            }
            <Row noGutters className={'w-100'}>
                <Col xs={12} className={'h-100 mb-3 mt-3'}>
                    <Card
                        bizkey={BizKeys.aimKey}
                        onKeyClicked={onKeyClicked}
                        selectedKey={selectedKey}
                        jsonContent={jsonContent.bizkey?.aimKey}
                    />
                </Col>
                <Col xs={12} className={'h-100 mb-3'}>
                    <Card
                        bizkey={BizKeys.actKey}
                        onKeyClicked={onKeyClicked}
                        selectedKey={selectedKey}
                        jsonContent={jsonContent.bizkey?.actKey}
                    />
                </Col>
                <Col xs={12} className={'h-100 mb-3'}>
                    <Card
                        bizkey={BizKeys.infiniteKey}
                        onKeyClicked={onKeyClicked}
                        selectedKey={selectedKey}
                        jsonContent={jsonContent.bizkey?.infiniteKey}
                    />
                </Col>
            </Row>
        </div>
    );
}

const Card = ({bizkey, onKeyClicked, selectedKey, jsonContent, className}) => {
    const focus = selectedKey === bizkey;
    const logoSize = {width: 110, height: 60};

    return (
        <div
            className={classnames('bizlearn-card h-100 transition', className, bizkey === BizKeys.aimKey
                ? (focus ? 'border-orange' : 'border-orange-light')
                : bizkey === BizKeys.actKey
                    ? (focus ? 'border-green' : 'border-green-light')
                    : (focus ? 'border-blue' : 'border-blue-light')
            )}
            onClick={() => onKeyClicked(bizkey, true)}
        >
            <div className={'d-flex flex-column'}>
                <BizPhaseName
                    bizkey={bizkey}
                    selectedKey={selectedKey}
                />
                <p className={'text-xs soft-wrap font-weight-medium text-wrap mt-2'}>
                    {jsonContent?.message ?? ""}
                </p>
            </div>
            <div className={'d-flex flex-column flex-grow-0 position-relative ml-auto'}>
                {
                    bizkey === BizKeys.aimKey &&
                    <AimKey
                        {...(selectedKey !== bizkey ? {opacity: '0.3'} : {})}
                        className={'transition cursor-pointer-hover'}
                        onClick={() => onKeyClicked(bizkey)}
                        style={logoSize}
                    />
                }
                {
                    bizkey === BizKeys.actKey &&
                    <ActKey
                        {...(selectedKey !== bizkey ? {opacity: '0.3'} : {})}
                        className={'transition cursor-pointer-hover'}
                        style={logoSize}
                    />
                }
                {
                    bizkey === BizKeys.infiniteKey &&
                    <InfiniteKey
                        {...(selectedKey !== bizkey ? {opacity: '0.3'} : {})}
                        className={'transition cursor-pointer-hover'}
                        style={logoSize}
                    />
                }
            </div>
        </div>
    )
}


const BizPhaseName = ({bizkey, selectedKey, onClick}) => {
    let color, padding;
    const focus = selectedKey === bizkey;
    switch (bizkey) {
        case BizKeys.aimKey:
            color = focus ? 'bg-orange' : 'bg-orange-light';
            padding = 'px-4-5';
            break;
        case BizKeys.actKey:
            color = focus ? 'bg-green' : 'bg-green-light';
            padding = 'px-4-5';
            break;
        case BizKeys.infiniteKey:
            color = focus ? 'bg-blue' : 'bg-blue-light';
            padding = 'px-4-5';
            break;
    }

    return (
        <div className={classnames('py-0 bg-white')} style={{width: 'fit-content'}}>
            <div className={classnames(color, padding, 'transition')}>
                <p>
                    <span className={'black font-weight-bold text-xs '}>{bizkey.name}</span>
                    <span className={'white font-weight-bold text-xs'}>KEY</span>
                </p>
            </div>
        </div>

    );
}


export default BaseAuthView;
